import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, Divider } from 'antd';
import { LinkOutlined, UserOutlined, EyeOutlined, BarChartOutlined, TeamOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { statisticsService } from '../services/statistics';
import { linkService } from '../services/link';
import { userApi } from '../services/user';
import { useAuthStore } from '../stores/authStore';

const { Title, Text } = Typography;

interface DashboardData {
  today: {
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
    realtime_visits: number;
  };
  yesterday: {
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
    realtime_visits: number;
  };

}

interface DashboardState {
  dashboardData: DashboardData | null;
  totalLinks: number;
  totalUsers: number;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, suffix }) => (
  <Card
    hoverable
    style={{
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--box-shadow-sm)',
      transition: 'var(--transition-smooth)',
      border: '1px solid var(--color-border-light)',
      background: 'var(--color-bg-primary)',
      overflow: 'hidden'
    }}
    bodyStyle={{ padding: '24px' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
      <Text style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{title}</Text>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: 'var(--border-radius-base)',
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        fontSize: '18px'
      }}>
        {icon}
      </div>
    </div>
    <div style={{
      fontSize: 28,
      fontWeight: 700,
      color: 'var(--color-text-primary)',
      lineHeight: 1.2,
      marginBottom: '4px'
    }}>
      {value}{suffix}
    </div>
  </Card>
);

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [dashboardState, setDashboardState] = useState<DashboardState>({
    dashboardData: null,
    totalLinks: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);

  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);
  // 用于防抖的定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadDashboardData = useCallback(async () => {
    console.log('loadDashboardData called at:', new Date().toISOString());

    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    // 设置防抖延迟
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // 并行获取所有数据
        const [dashboardData, linksData] = await Promise.all([
          statisticsService.getDashboard(),
          linkService.getLinks(),
        ]);

        // 检查请求是否被取消
        if (!abortControllerRef.current?.signal.aborted) {
          console.log('📊 设置仪表板状态:');
          console.log('- dashboardData:', dashboardData);
          console.log('- totalLinks:', linksData.total || 0);

          setDashboardState({
            dashboardData,
            totalLinks: linksData.total || 0,
            totalUsers: 0, // 不再需要单独获取用户数据
          });
          setLoading(false);
        }
      } catch (error: any) {
        // 忽略被取消的请求错误
        if (error.name !== 'AbortError') {
          console.error('加载仪表板数据失败:', error);
          setLoading(false);
        }
      }
    }, 100); // 100ms 防抖延迟
  }, []);

  useEffect(() => {
    loadDashboardData();

    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: 'var(--color-text-secondary)' }}>
          加载仪表板数据中...
        </div>
      </div>
    );
  }

  const today = dashboardState.dashboardData?.today;
  const yesterday = dashboardState.dashboardData?.yesterday;

  return (
    <div style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
      {/* 页面标题 */}
      <div style={{
        background: 'var(--color-bg-primary)',
        padding: '24px 32px',
        borderRadius: 'var(--border-radius-lg)',
        marginBottom: '24px',
        boxShadow: 'var(--box-shadow-sm)',
        border: '1px solid var(--color-border-light)'
      }}>
        <Title level={2} style={{ margin: 0, marginBottom: '8px', fontSize: '28px', fontWeight: 700, color: 'var(--color-text-primary)' }}>仪表板</Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>欢迎使用访客管理平台</Text>
      </div>

      {/* 今日数据 */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--border-radius-base)',
              background: 'var(--color-primary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-primary)'
            }}>
              <EyeOutlined />
            </div>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>今日数据</span>
          </div>
        }
        style={{
          marginBottom: 24,
          borderRadius: 'var(--border-radius-lg)',
          border: '1px solid var(--color-border-light)',
          boxShadow: 'var(--box-shadow-sm)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="今日访问量"
              value={today?.total_visits || 0}
              icon={<EyeOutlined />}
              color="var(--color-primary)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="默认跳转"
              value={today?.default_redirects || 0}
              icon={<LinkOutlined />}
              color="var(--color-success)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="拦截跳转"
              value={today?.intercept_redirects || 0}
              icon={<BarChartOutlined />}
              color="var(--color-error)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="成功率"
              value= {Number((today?.success_rate ?? 0).toFixed(2))}
              icon={<UserOutlined />}
              color="#a855f7"
              suffix="%"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="总链接数"
              value={dashboardState.totalLinks}
              icon={<TeamOutlined />}
              color="var(--color-warning)"
            />
          </Col>
        </Row>
      </Card>

      {/* 昨日数据 */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--border-radius-base)',
              background: '#f4f4f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-secondary)'
            }}>
              <BarChartOutlined />
            </div>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>昨日数据</span>
          </div>
        }
        style={{
          marginBottom: 24,
          borderRadius: 'var(--border-radius-lg)',
          border: '1px solid var(--color-border-light)',
          boxShadow: 'var(--box-shadow-sm)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="昨日访问量"
              value={yesterday?.total_visits || 0}
              icon={<EyeOutlined />}
              color="var(--color-primary)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="默认跳转"
              value={yesterday?.default_redirects || 0}
              icon={<LinkOutlined />}
              color="var(--color-success)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="拦截跳转"
              value={yesterday?.intercept_redirects || 0}
              icon={<BarChartOutlined />}
              color="var(--color-error)"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <StatCard
              title="成功率"
              value={Number((yesterday?.success_rate ?? 0).toFixed(2))}
              icon={<UserOutlined />}
              color="#a855f7"
              suffix="%"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4.8}>
            <Card
              style={{
                background: 'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-tertiary) 100%)',
                borderRadius: 'var(--border-radius-lg)',
                border: '1px solid var(--color-border-light)',
                boxShadow: 'var(--box-shadow-sm)',
                transition: 'var(--transition-smooth)'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>对比昨日</Text>
              <div style={{ marginTop: 12 }}>
                {today && yesterday && (
                  <Text
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: (today.total_visits - yesterday.total_visits) >= 0
                        ? 'var(--color-success)'
                        : 'var(--color-error)'
                    }}
                  >
                    {today.total_visits - yesterday.total_visits >= 0 ? '+' : ''}
                    {today.total_visits - yesterday.total_visits}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 系统状态 - 仅管理员可见 */}
      {user?.user_type === 'admin' && <Card
        style={{
          background: 'var(--gradient-ocean)',
          color: '#fff',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--box-shadow-md)',
          border: 'none',
          overflow: 'hidden',
          position: 'relative'
        }}
        bodyStyle={{ padding: '32px', position: 'relative', zIndex: 1 }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />
        <Title level={4} style={{ color: '#fff', marginBottom: '24px', fontWeight: 600 }}>系统状态</Title>
        <Row gutter={24}>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                fontSize: 48,
                fontWeight: 700,
                marginBottom: 12,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>✓</div>
              <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: 16, fontWeight: 500 }}>服务运行正常</Text>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.95)' }}>
                {dashboardState.totalLinks}
              </div>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 500 }}>活跃链接</Text>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.95)' }}>
                {(today?.total_visits || 0) + (yesterday?.total_visits || 0)}
              </div>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 500 }}>48小时访问量</Text>
            </div>
          </Col>
        </Row>
      </Card>}
    </div>
  );
};

export default Dashboard;